import { useState, useEffect, useCallback } from "react";
import { crewOpsApi } from "../../api";
import { toast } from "sonner";
import {
  CalendarDays, RefreshCw, CheckCircle, Users, Plane,
  Clock, AlertTriangle, ChevronDown, ChevronUp, Rocket,
  CheckSquare, Square, Globe, FileText, Shield,
} from "lucide-react";
import ManagementSignoff from "./ManagementSignoff";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const DAY_COLORS = {
  scheduled: { bg: "bg-amber-500/10", border: "border-amber-500/25", text: "text-amber-300", dot: "bg-amber-400" },
  crewed:    { bg: "bg-teal-500/10",  border: "border-teal-500/25",  text: "text-teal-300",  dot: "bg-teal-400"  },
  base_approved: { bg: "bg-emerald-500/10", border: "border-emerald-500/25", text: "text-emerald-300", dot: "bg-emerald-400" },
};
const STATUS_LABEL = {
  scheduled: "Scheduled", crewed: "Crewed", base_approved: "Base Approved", pending: "Pending",
};

// ── Config Step ───────────────────────────────────────────────────────────────
function ConfigPanel({ onGenerated }) {
  const [scenarios, setScenarios]       = useState([]);
  const [selectedId, setSelectedId]     = useState("");
  const [opDays, setOpDays]             = useState([...WEEKDAYS]);
  const [preview, setPreview]           = useState(null);
  const [generating, setGenerating]     = useState(false);
  const [loadingScen, setLoadingScen]   = useState(true);

  useEffect(() => {
    crewOpsApi.listPairingScenarios().then(r => {
      const valid = (r.data || []).filter(s => s.schedule?.tail_number);
      setScenarios(valid);
      if (valid.length) setSelectedId(valid[0].id);
    }).catch(() => {}).finally(() => setLoadingScen(false));
  }, []);

  const selectedScen = scenarios.find(s => s.id === selectedId);

  // Compute preview count when days change
  useEffect(() => {
    if (!selectedScen) { setPreview(null); return; }
    const rawMonth = selectedScen.schedule?.month || "";
    let yr, mo;
    try {
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const parts = rawMonth.split(" ");
      if (rawMonth.includes("-")) {
        // "YYYY-MM" format
        [yr, mo] = rawMonth.split("-").map(Number);
      } else if (parts.length === 2) {
        // "Month Year" format e.g. "April 2026"
        mo = months.indexOf(parts[0]) + 1;
        yr = parseInt(parts[1]);
      } else if (parts.length === 1 && months.indexOf(parts[0]) >= 0) {
        // Just month name e.g. "April" — use current year
        mo = months.indexOf(parts[0]) + 1;
        yr = new Date().getFullYear();
      }
    } catch { yr = new Date().getFullYear(); mo = new Date().getMonth() + 1; }
    if (!yr || !mo) { setPreview(null); return; }
    const daysInMonth = new Date(yr, mo, 0).getDate();
    const dayIdxs = new Set(opDays.map(d => DAYS.indexOf(d)));
    let count = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      if (dayIdxs.has(new Date(yr, mo - 1, day).getDay() === 0 ? 6 : new Date(yr, mo - 1, day).getDay() - 1)) count++;
    }
    const bh = (selectedScen.schedule?.total_scheduled_bh || 0) + (selectedScen.schedule?.total_extra_bh || 0);
    setPreview({ count, bh: Math.round(bh * count * 10) / 10, month: rawMonth });
  }, [opDays, selectedId, selectedScen]);

  const toggleDay = (d) => setOpDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const setPreset = (preset) => {
    if (preset === "weekdays") setOpDays([...WEEKDAYS]);
    else if (preset === "all")  setOpDays([...DAYS]);
    else if (preset === "weekend") setOpDays(["Sat", "Sun"]);
  };

  const handleGenerate = async () => {
    if (!selectedId) { toast.error("Select a scenario first"); return; }
    if (!opDays.length) { toast.error("Select at least one operating day"); return; }
    setGenerating(true);
    try {
      const r = await crewOpsApi.generateMonthlySchedule(selectedId, { operating_days: opDays });
      toast.success(`Generated ${r.data.total_operating_days} operating days for ${r.data.tail_number}`);
      onGenerated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="border border-border/40 rounded-sm bg-card/20 p-5 space-y-5" data-testid="config-panel">
      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">1 — Select Scenario</div>
        {loadingScen ? (
          <div className="text-xs text-muted-foreground animate-pulse">Loading scenarios...</div>
        ) : scenarios.length === 0 ? (
          <div className="text-xs text-amber-400">No saved scenarios with complete schedules. Go to Pairings → Route Map → save a scenario first.</div>
        ) : (
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="scenario-select">
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>
                {s.schedule?.tail_number} · {s.name} · {s.base}
                {s.schedule?.extra_legs?.length ? ` (+${s.schedule.extra_legs.length} extra legs)` : ""}
              </option>
            ))}
          </select>
        )}
        {selectedScen && (
          <div className="mt-2 flex items-center gap-4 text-[10px] text-muted-foreground">
            <span className="font-mono text-amber-400">{selectedScen.schedule?.tail_number}</span>
            <span>{selectedScen.base} Base</span>
            <span>·</span>
            <span>{selectedScen.schedule?.month}</span>
            <span>·</span>
            <span className={`font-bold ${selectedScen.schedule?.extra_legs?.length ? "text-red-400" : "text-teal-400"}`}>
              {selectedScen.schedule?.extra_legs?.length ? "Dual Crew" : "Single Crew"}
            </span>
          </div>
        )}
      </div>

      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">2 — Operating Days</div>
        <div className="flex items-center gap-1.5 mb-2">
          {DAYS.map(d => (
            <button key={d} onClick={() => toggleDay(d)}
              className={`px-3 py-1.5 text-[10px] font-bold rounded-sm border transition-all ${
                opDays.includes(d)
                  ? "bg-primary/15 border-primary/40 text-primary"
                  : "bg-background border-border/30 text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`day-toggle-${d}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {[["Weekdays", "weekdays"], ["All Week", "all"], ["Weekend", "weekend"]].map(([label, preset]) => (
            <button key={preset} onClick={() => setPreset(preset)}
              className="text-[9px] px-2 py-1 border border-border/25 rounded-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all">
              {label}
            </button>
          ))}
        </div>
      </div>

      {preview && (
        <div className="flex items-center gap-6 px-4 py-3 rounded-sm border border-indigo-500/20 bg-indigo-500/5" data-testid="preview-card">
          <div className="text-center">
            <div className="text-2xl font-black text-indigo-300">{preview.count}</div>
            <div className="text-[9px] text-muted-foreground">operating days</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-amber-300">{preview.bh}</div>
            <div className="text-[9px] text-muted-foreground">block hours</div>
          </div>
          <div className="flex-1 text-[10px] text-muted-foreground">
            <div className="font-bold text-foreground mb-0.5">{preview.month}</div>
            <div>Operating: {opDays.join(", ")}</div>
          </div>
          <button onClick={handleGenerate} disabled={generating || !selectedId}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-all"
            data-testid="generate-btn">
            {generating ? <RefreshCw size={12} className="animate-spin" /> : <CalendarDays size={12} />}
            {generating ? "Generating…" : "Generate Schedule"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Calendar Grid ─────────────────────────────────────────────────────────────
function CalendarGrid({ schedule, onAllocate, onApproveBase, allocating }) {
  const [expandedDate, setExpandedDate]   = useState(null);
  const [approving, setApproving]         = useState(false);
  const [approver, setApprover]           = useState("Base Operations Manager");
  const [comments, setComments]           = useState("");
  const [showApproval, setShowApproval]   = useState(false);

  const instances = schedule.daily_instances || [];
  const month     = schedule.month || "";
  const allocated = schedule.crew_allocation_status !== "pending";

  // Build calendar: weeks array
  const byDate = Object.fromEntries(instances.map(i => [i.date, i]));
  const allDates = instances.map(i => i.date).sort();
  const firstDate = allDates[0] ? new Date(allDates[0]) : new Date();
  const [yr, mo] = month.split("-").map(Number);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const firstDayOfMonth = new Date(yr, mo - 1, 1).getDay(); // 0=Sun
  // Pad to Monday start: shift by (0=Mon)
  const startOffset = (firstDayOfMonth + 6) % 7;

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${yr}-${String(mo).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    cells.push({ date: dateStr, instance: byDate[dateStr] || null });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const handleApproveBase = async () => {
    setApproving(true);
    try {
      await onApproveBase(schedule.id, { approved_by: approver, comments, base: schedule.base });
      toast.success(`${schedule.base} base schedule approved`);
      setShowApproval(false);
    } catch { toast.error("Approval failed"); }
    finally { setApproving(false); }
  };

  return (
    <div className="space-y-4" data-testid={`calendar-grid-${schedule.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-black text-sm text-foreground">{schedule.tail_number}</span>
            <span className="text-[9px] font-bold text-muted-foreground px-2 py-0.5 bg-white/5 border border-border/20 rounded-sm">{schedule.base}</span>
            <span className="text-[9px] font-mono text-amber-400/80">{schedule.scenario_name}</span>
            {schedule.requires_dual_crew && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border bg-red-500/10 border-red-500/25 text-red-300 flex items-center gap-1">
                <AlertTriangle size={8} /> Dual Crew
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
            <span>Operating: {(schedule.operating_days || []).join(", ")}</span>
            <span>·</span>
            <span>{schedule.total_operating_days} days</span>
            <span>·</span>
            <span>{schedule.total_block_hours}h block total</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!allocated && (
            <button onClick={() => onAllocate(schedule.id)} disabled={allocating}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm bg-teal-500/15 border border-teal-500/30 text-teal-300 hover:bg-teal-500/25 disabled:opacity-40 transition-all"
              data-testid={`allocate-btn-${schedule.id}`}>
              {allocating ? <RefreshCw size={11} className="animate-spin" /> : <Users size={11} />}
              {allocating ? "Allocating…" : "Allocate Crew"}
            </button>
          )}
          {allocated && !schedule.base_approval && (
            <button onClick={() => setShowApproval(s => !s)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20 transition-all"
              data-testid={`approve-base-btn-${schedule.id}`}>
              <CheckCircle size={11} /> Approve Base Schedule
            </button>
          )}
          {schedule.base_approval && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm border bg-emerald-500/10 border-emerald-500/25 text-emerald-300">
              <CheckCircle size={10} /> Base Approved
            </span>
          )}
        </div>
      </div>

      {/* Approval form */}
      {showApproval && (
        <div className="flex items-end gap-3 px-4 py-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
          <div className="flex-1">
            <label className="text-[9px] text-muted-foreground block mb-0.5">Approver</label>
            <input value={approver} onChange={e => setApprover(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none"
              data-testid="base-approver-input" />
          </div>
          <div className="flex-1">
            <label className="text-[9px] text-muted-foreground block mb-0.5">Comments</label>
            <input value={comments} onChange={e => setComments(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none" />
          </div>
          <button onClick={handleApproveBase} disabled={approving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40 transition-all">
            {approving ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle size={11} />}
            {approving ? "Approving…" : "Confirm"}
          </button>
        </div>
      )}

      {/* Calendar grid */}
      <div>
        <div className="grid grid-cols-7 gap-px mb-px">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[9px] font-bold text-muted-foreground/60 uppercase tracking-widest py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-border/10">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={idx} className="bg-background/30 h-16" />;
            const { date, instance } = cell;
            const dayNum = parseInt(date.split("-")[2]);
            const st = instance?.status || null;
            const col = st ? (DAY_COLORS[st] || DAY_COLORS.scheduled) : null;
            const isExpanded = expandedDate === date;
            const crew = instance?.crew;

            return (
              <div key={date}
                onClick={() => instance && setExpandedDate(isExpanded ? null : date)}
                className={`relative p-1.5 h-16 border flex flex-col justify-between transition-all ${
                  instance
                    ? `${col?.bg} ${col?.border} cursor-pointer hover:brightness-110`
                    : "bg-background/20 border-border/10"
                } ${isExpanded ? "ring-1 ring-primary" : ""}`}
                data-testid={`cal-cell-${date}`}>
                <div className="flex items-start justify-between">
                  <span className={`text-[10px] font-black ${instance ? col?.text : "text-muted-foreground/30"}`}>
                    {dayNum}
                  </span>
                  {st && <div className={`w-1.5 h-1.5 rounded-full ${col?.dot}`} />}
                </div>
                {instance && (
                  <div className="space-y-0.5">
                    <div className="text-[7px] font-mono text-muted-foreground/60 leading-tight truncate">
                      {instance.sectors?.slice(0,2).map(s => `${s.dep}→${s.arr}`).join(" ")}
                    </div>
                    {crew?.captain?.name && (
                      <div className="text-[7px] text-teal-300/70 truncate">
                        CPT {crew.captain.name.split(" ").slice(-1)[0]}
                      </div>
                    )}
                    {crew?.dual_crew && crew?.crew_1?.captain?.name && (
                      <div className="text-[7px] text-amber-300/70 truncate">
                        C1: {crew.crew_1.captain.name.split(" ").slice(-1)[0]}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded day detail */}
      {expandedDate && byDate[expandedDate] && (
        <DayDetail instance={byDate[expandedDate]} onClose={() => setExpandedDate(null)} />
      )}

      {/* Legend */}
      <div className="flex items-center gap-4">
        {[["scheduled","Scheduled"], ["crewed","Crewed"], ["base_approved","Base Approved"]].map(([s, l]) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${DAY_COLORS[s].dot}`} />
            <span className="text-[9px] text-muted-foreground">{l}</span>
          </div>
        ))}
        <div className="ml-auto text-[9px] text-muted-foreground/50">Click a cell to expand crew details</div>
      </div>
    </div>
  );
}

// ── Day Detail Popup ──────────────────────────────────────────────────────────
function DayDetail({ instance, onClose }) {
  const crew     = instance.crew || {};
  const isDual   = crew.dual_crew;
  const sectors  = instance.sectors || [];

  return (
    <div className="border border-border/40 rounded-sm bg-card/40 p-4 space-y-3" data-testid={`day-detail-${instance.date}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-foreground">
          {new Date(instance.date + "T12:00:00Z").toLocaleDateString("en-IN", { weekday:"long", day:"2-digit", month:"long", year:"numeric" })}
        </div>
        <div className="flex items-center gap-2">
          {isDual && <span className="text-[9px] font-bold px-2 py-0.5 rounded-sm border bg-red-500/10 border-red-500/25 text-red-300">Dual Crew</span>}
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-sm border ${DAY_COLORS[instance.status]?.bg} ${DAY_COLORS[instance.status]?.border} ${DAY_COLORS[instance.status]?.text}`}>
            {STATUS_LABEL[instance.status] || instance.status}
          </span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-[10px]">✕</button>
        </div>
      </div>

      {/* Sectors */}
      <div className="flex flex-wrap gap-1.5">
        {sectors.map((s, i) => (
          <span key={i} className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 bg-amber-500/8 border border-amber-500/20 rounded-sm text-amber-300">
            <Plane size={8} /> {s.dep}→{s.arr} <span className="text-amber-400/50">{s.depTime}–{s.arrTime}</span>
          </span>
        ))}
      </div>

      {/* Crew */}
      {!isDual && crew.captain?.name && (
        <div className="grid grid-cols-5 gap-1.5">
          {[
            { label: "Captain",        person: crew.captain },
            { label: "First Officer",  person: crew.first_officer },
            ...(crew.cabin_crew || []).map((cc, i) => ({ label: cc.role || `CC${i+1}`, person: cc })),
          ].map(({ label, person }) => person?.name ? (
            <div key={label} className="px-2 py-1.5 bg-white/[0.03] border border-border/20 rounded-sm">
              <div className="text-[8px] text-muted-foreground uppercase tracking-widest">{label}</div>
              <div className="text-[10px] font-bold text-foreground truncate">{person.name}</div>
              <div className={`text-[9px] font-mono font-bold ${(person.fatigue_score || 0) > 65 ? "text-amber-400" : "text-teal-400"}`}>
                FS {person.fatigue_score ?? "—"}
              </div>
            </div>
          ) : null)}
        </div>
      )}

      {isDual && (
        <div className="grid grid-cols-2 gap-3">
          {[["Crew 1 — Phase 1", "crew_1", "amber"], ["Crew 2 — Phase 2", "crew_2", "indigo"]].map(([label, key, col]) => {
            const c = crew[key] || {};
            return (
              <div key={key} className={`border border-${col}-500/20 bg-${col}-500/5 rounded-sm p-2`}>
                <div className={`text-[8px] font-bold text-${col}-400 uppercase tracking-widest mb-1.5`}>{label}</div>
                <div className="space-y-1">
                  {[{l:"Capt",p:c.captain},{l:"FO",p:c.first_officer},...(c.cabin_crew||[]).map((cc,i)=>({l:cc.role||`CC${i+1}`,p:cc}))].map(({l,p}) =>
                    p?.name ? (
                      <div key={l} className="flex items-center justify-between text-[9px]">
                        <span className="text-muted-foreground">{l}</span>
                        <span className="text-foreground font-semibold truncate max-w-[8rem]">{p.name}</span>
                        <span className={`font-mono ${(p.fatigue_score||0)>65?"text-amber-400":"text-teal-400"}`}>FS{p.fatigue_score}</span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[9px] text-muted-foreground/50">FDP: <span className="font-mono text-foreground/70">{instance.fdp_hours?.toFixed(1)}h</span></div>
    </div>
  );
}

// ── Fleet Summary ─────────────────────────────────────────────────────────────
function FleetSummary({ month, onPublish }) {
  const [fleetData, setFleetData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await crewOpsApi.getFleetRoster({ month });
      setFleetData(r.data);
    } catch { toast.error("Failed to load fleet roster"); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { if (month) load(); }, [load, month]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await crewOpsApi.publishFleetSchedule({ month });
      toast.success("Fleet schedule published!");
      load();
      if (onPublish) onPublish();
    } catch { toast.error("Publish failed"); }
    finally { setPublishing(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-xs animate-pulse">Loading fleet roster...</div>;
  if (!fleetData || fleetData.total_aircraft === 0) return (
    <div className="text-center py-12 space-y-2">
      <Globe size={32} className="text-muted-foreground/20 mx-auto" />
      <div className="text-sm text-muted-foreground">No monthly schedules found for {month}</div>
    </div>
  );

  const { bases, total_aircraft, total_days, total_block_hours, approved_count, all_approved } = fleetData;

  return (
    <div className="space-y-5" data-testid="fleet-summary">
      {/* Fleet KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Aircraft", total_aircraft,       "text-amber-300"],
          ["Operating Days", total_days,     "text-indigo-300"],
          ["Total Block Hrs", `${total_block_hours}h`, "text-teal-300"],
          ["Base Approved", `${approved_count}/${total_aircraft}`, all_approved ? "text-emerald-300" : "text-amber-300"],
        ].map(([l, v, c]) => (
          <div key={l} className="border border-border/30 rounded-sm bg-white/[0.02] px-4 py-3 text-center">
            <div className={`text-2xl font-black ${c}`}>{v}</div>
            <div className="text-[9px] text-muted-foreground uppercase tracking-widest">{l}</div>
          </div>
        ))}
      </div>

      {/* Per-base tables */}
      {Object.entries(bases).map(([base, schedules]) => {
        const baseApproved = schedules.every(s => s.base_approval);
        return (
          <div key={base} className="border border-border/30 rounded-sm overflow-hidden">
            <div className={`flex items-center justify-between px-4 py-2 border-b border-border/20 ${baseApproved ? "bg-emerald-500/5" : "bg-white/[0.02]"}`}>
              <div className="flex items-center gap-3">
                <span className="text-sm font-black text-foreground">{base} Base</span>
                <span className="text-[10px] text-muted-foreground">{schedules.length} aircraft</span>
              </div>
              {baseApproved
                ? <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-300"><CheckCircle size={10} /> Base Approved</span>
                : <span className="text-[10px] text-amber-400">{schedules.filter(s => s.base_approval).length}/{schedules.length} approved</span>
              }
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-border/20">
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-semibold">Aircraft</th>
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-semibold">Scenario</th>
                    <th className="text-center px-3 py-1.5 text-muted-foreground font-semibold">Op Days</th>
                    <th className="text-center px-3 py-1.5 text-muted-foreground font-semibold">Block Hrs</th>
                    <th className="text-center px-3 py-1.5 text-muted-foreground font-semibold">Crew Mode</th>
                    <th className="text-center px-3 py-1.5 text-muted-foreground font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(s => {
                    const st = s.crew_allocation_status || "pending";
                    const stCol = st === "base_approved" ? "text-emerald-400" : st === "allocated" ? "text-teal-400" : "text-amber-400";
                    return (
                      <tr key={s.id} className="border-b border-border/10 hover:bg-white/[0.02]">
                        <td className="px-3 py-1.5 font-mono font-bold text-amber-400">{s.tail_number}</td>
                        <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[120px]">{s.scenario_name}</td>
                        <td className="px-3 py-1.5 text-center font-bold text-foreground">{s.total_operating_days}</td>
                        <td className="px-3 py-1.5 text-center font-mono text-indigo-300">{s.total_block_hours}h</td>
                        <td className="px-3 py-1.5 text-center">
                          {s.requires_dual_crew
                            ? <span className="text-[8px] font-bold text-red-300 border border-red-500/25 px-1.5 py-0.5 rounded-sm">DUAL</span>
                            : <span className="text-[8px] font-bold text-teal-300 border border-teal-500/25 px-1.5 py-0.5 rounded-sm">SINGLE</span>
                          }
                        </td>
                        <td className={`px-3 py-1.5 text-center font-bold ${stCol}`}>{st.replace("_", " ").toUpperCase()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Fleet Publish */}
      {all_approved && !fleetData.fleet_published && (
        <div className="flex items-center justify-between px-5 py-4 border border-indigo-500/25 bg-indigo-500/5 rounded-sm">
          <div>
            <div className="text-sm font-bold text-indigo-300">All bases approved — publish fleet schedule?</div>
            <div className="text-[10px] text-muted-foreground/60 mt-0.5">This will publish the complete {month} schedule for all aircraft across all bases.</div>
          </div>
          <button onClick={handlePublish} disabled={publishing}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 text-xs font-bold rounded-sm hover:bg-indigo-500/30 disabled:opacity-40 transition-all"
            data-testid="publish-fleet-btn">
            {publishing ? <RefreshCw size={12} className="animate-spin" /> : <Rocket size={12} />}
            {publishing ? "Publishing…" : "Publish Fleet Schedule"}
          </button>
        </div>
      )}
      {fleetData.fleet_published && (
        <div className="flex items-center gap-3 px-5 py-3 border border-emerald-500/25 bg-emerald-500/5 rounded-sm">
          <CheckCircle size={14} className="text-emerald-400" />
          <span className="text-sm font-bold text-emerald-300">Fleet Schedule Published — {month}</span>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MonthlyScheduler() {
  const [activeTab, setActiveTab]       = useState("configure");
  const [schedules, setSchedules]       = useState([]);
  const [loadingList, setLoadingList]   = useState(true);
  const [allocatingId, setAllocatingId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const loadSchedules = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await crewOpsApi.listMonthlySchedules({ month: selectedMonth });
      setSchedules(r.data || []);
    } catch { }
    finally { setLoadingList(false); }
  }, [selectedMonth]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const handleGenerated = (doc) => {
    setSchedules(prev => [doc, ...prev]);
    setActiveTab("schedules");
    // Fetch full with daily_instances
    crewOpsApi.getMonthlySchedule(doc.id).then(r => {
      setSchedules(prev => prev.map(s => s.id === doc.id ? r.data : s));
    }).catch(() => {});
  };

  const handleAllocate = async (scheduleId) => {
    setAllocatingId(scheduleId);
    try {
      const r = await crewOpsApi.allocateMonthlyCrewAll(scheduleId);
      setSchedules(prev => prev.map(s => s.id === scheduleId ? r.data : s));
      toast.success("Crew allocated for all operating days");
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocation failed"); }
    finally { setAllocatingId(null); }
  };

  const handleApproveBase = async (scheduleId, data) => {
    const r = await crewOpsApi.approveBaseSchedule(scheduleId, data);
    setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, ...r.data } : s));
  };

  const TABS = [
    { id: "configure", label: "Configure",       icon: CalendarDays },
    { id: "schedules", label: `Schedules (${schedules.length})`, icon: Globe },
    { id: "fleet",     label: "Fleet Roster",    icon: Shield },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background" data-testid="monthly-scheduler-page">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-5 pb-0">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-foreground">Monthly Schedule Generator</h2>
            <p className="text-[11px] text-muted-foreground">
              Select operating days → generate monthly instances → allocate crew with rotation → per-base sign-off → fleet publish
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[9px] text-muted-foreground">Month</label>
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
              className="bg-card border border-border rounded-sm px-2 py-1 text-xs font-mono text-foreground focus:outline-none"
              data-testid="month-picker" />
            <button onClick={loadSchedules} className="p-1.5 border border-border/40 rounded-sm text-muted-foreground hover:text-foreground transition-colors">
              <RefreshCw size={12} className={loadingList ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold rounded-t-sm border-b-2 transition-all ${
                activeTab === id
                  ? "text-primary border-primary bg-primary/5"
                  : "text-muted-foreground border-transparent hover:text-foreground"
              }`}
              data-testid={`scheduler-tab-${id}`}>
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
        <div className="h-px bg-border/20" />
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {activeTab === "configure" && (
          <div className="max-w-2xl mx-auto">
            <ConfigPanel onGenerated={handleGenerated} />

            {schedules.length > 0 && (
              <div className="mt-6">
                <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                  Existing Schedules This Month
                </div>
                <div className="space-y-2">
                  {schedules.slice(0, 3).map(s => (
                    <div key={s.id} className="flex items-center gap-3 px-3 py-2 border border-border/25 rounded-sm bg-white/[0.02] text-[10px]">
                      <span className="font-mono font-bold text-amber-400">{s.tail_number}</span>
                      <span className="text-muted-foreground">{s.scenario_name}</span>
                      <span className="ml-auto text-muted-foreground">{s.total_operating_days}d</span>
                      <span className={`font-bold ${s.crew_allocation_status === "base_approved" ? "text-emerald-400" : s.crew_allocation_status === "allocated" ? "text-teal-400" : "text-amber-400"}`}>
                        {s.crew_allocation_status?.replace("_"," ").toUpperCase()}
                      </span>
                      <button onClick={() => setActiveTab("schedules")} className="text-muted-foreground/50 hover:text-primary text-[9px]">View →</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "schedules" && (
          <div className="space-y-8 max-w-5xl mx-auto">
            {loadingList ? (
              <div className="text-center py-12 text-muted-foreground animate-pulse text-sm">Loading schedules...</div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-16 space-y-3 border border-border/20 rounded-sm bg-white/[0.01]">
                <CalendarDays size={32} className="text-muted-foreground/20 mx-auto" />
                <div className="text-sm text-muted-foreground">No schedules generated yet for {selectedMonth}</div>
                <div className="text-[11px] text-muted-foreground/50">Go to Configure tab to generate your first monthly schedule</div>
              </div>
            ) : (
              schedules.map(s => (
                <CalendarGrid
                  key={s.id}
                  schedule={s}
                  onAllocate={handleAllocate}
                  onApproveBase={handleApproveBase}
                  allocating={allocatingId === s.id}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "fleet" && (
          <div className="max-w-5xl mx-auto">
            <FleetSummary month={selectedMonth} onPublish={loadSchedules} />
          </div>
        )}
      </div>
    </div>
  );
}
